import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router';

import { CapturePage } from './routes/CapturePage.jsx';
import { HomePage } from './routes/HomePage.jsx';
import { IndexRedirect } from './routes/IndexRedirect.jsx';
import { LoginPage } from './routes/LoginPage.jsx';
import { PerfPage } from './routes/PerfPage.jsx';
import { SearchPage } from './routes/SearchPage.jsx';
import { RitualPage, ViewPage } from './routes/ViewPage.jsx';
import { AppLayout } from './shell/AppLayout.jsx';
import { FocusLayout } from './shell/FocusLayout.jsx';
import { TrashPage } from './trash/TrashPage.jsx';
import { TriagePage } from './triage/TriagePage.jsx';

/**
 * Routes (design D1): `/login` and `/perf` live outside the shell (no session / measure without
 * chrome); everything else renders inside the authenticated layout. The F0 test screens keep
 * working inside it until capture, search and the views get their own changes.
 */

const rootRoute = createRootRoute({ component: () => <Outlet /> });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const perfRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/perf',
  component: PerfPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: AppLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: IndexRedirect,
});

const viewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/v/$viewKey',
  component: ViewPage,
  // `?tarea=` keeps the task open in the detail panel across reloads and history (f1-detail-panel).
  validateSearch: (search: Record<string, unknown>): { tarea?: string } =>
    typeof search.tarea === 'string' && search.tarea !== '' ? { tarea: search.tarea } : {},
});

const ritualRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/r/$ritualKey',
  component: RitualPage,
});

const blocksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/blocks',
  component: HomePage,
});

const captureRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/capture',
  component: CapturePage,
});

const searchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/search',
  // `q` is the text (the palette hands it over); `tarea` the result open in the panel.
  validateSearch: (search: Record<string, unknown>): { q?: string; tarea?: string } => ({
    ...(typeof search.q === 'string' ? { q: search.q } : {}),
    ...(typeof search.tarea === 'string' && search.tarea !== '' ? { tarea: search.tarea } : {}),
  }),
  component: SearchPage,
});

const trashRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/trash',
  component: TrashPage,
});

// Full-screen modes (docs/05 §5.1): same session and database, no sidebar.
const focusRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'focus',
  component: FocusLayout,
});

const triageRoute = createRoute({
  getParentRoute: () => focusRoute,
  path: '/triage',
  component: TriagePage,
});

export const router = createRouter({
  routeTree: rootRoute.addChildren([
    loginRoute,
    perfRoute,
    appRoute.addChildren([
      indexRoute,
      viewRoute,
      ritualRoute,
      blocksRoute,
      captureRoute,
      searchRoute,
      trashRoute,
    ]),
    focusRoute.addChildren([triageRoute]),
  ]),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
